<template>
  <div>
    <ContentDisplay>
      <template #content-block-1>
        <div v-if="error" class="available-balance__error">
          <ButtonSecondary
            label="Atualizar"
            icon="EA0140"
            @click="emitOnRetry"
          />
        </div>

        <div v-else>
          <ListDisplay
            title="Saldo disponível"
            button-icon-label="Ver detalhes do saldo disponível"
            icon-name="EF0080"
            value=""
            show-icon
            align-vertical
            @click="openModal"
          >
            <template #value-slot>
              <SkeletonLoader
                v-if="loading"
                data-testid="available-balance-loader"
              >
                <div class="available-balance__loader" />
              </SkeletonLoader>
              <Money v-else width="70px">{{ availableToOperateValue }}</Money>
            </template>
          </ListDisplay>

          <SkeletonLoader v-if="isLoadingMarginAllocation">
            <div class="available-balance__margin-allocation-loader" />
          </SkeletonLoader>
          <Row
            v-else-if="!isLoadingMarginAllocation && shouldShowMarginAllocation"
            class="available-balance__margin-allocation-row"
            left-title="Limite Day Trade"
            chevron
            has-interaction
            has-line
            @click="goToMarginAllocation"
          >
            <template #top-right>
              <Money v-if="hasAllocatedValue" width="70px">{{
                marginAllocation?.balance.formatted
              }}</Money>
              <BaseText v-else class="available-balance__limit-day-trade-text">
                Alocar limite
              </BaseText>
            </template>
          </Row>
        </div>
      </template>
    </ContentDisplay>
    <AvailableBalanceModal
      :is-open="isModalOpen"
      :available-balance-data="availableBalanceData"
      :error="error"
      :loading="loading"
      @on-close="closeModal"
      @on-retry="emitOnRetry"
    />
    <MarginAllocationDrawer
      data-testid="margin-allocation-drawer"
      :margin-allocation="marginAllocation"
      :is-open="isMarginAllocationDrawerOpen"
      @close="isMarginAllocationDrawerOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';

import {
  ButtonSecondary,
  SkeletonLoader,
  ContentDisplay,
  ListDisplay,
  Row,
  BaseText
} from '@warrenbrasil/nebraska-web';
import { AvailableBalanceModal } from '@/modules/trade/components/AvailableBalanceModal';
import { Money } from '@/modules/common/components/MoneyV2';
import { AvailableBalanceData } from '@/modules/trade/services/unified-account/types';
import {
  getMarginAllocationV2,
  IMarginAllocationV2
} from '@/modules/trade/services/margin-allocation';
import { MarginAllocationDrawer } from '@/modules/trade/views/TradeView/components/TradeSidebar/components/MarginAllocationDrawer';
import { useRouter } from 'vue-router/composables';

const router = useRouter();

interface Props {
  availableBalanceData: AvailableBalanceData;
  hasMarginAllocation?: boolean;
  loading: boolean;
  error: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  hasMarginAllocation: false
});

interface Emits {
  'on-retry': () => void;
}
const emit = defineEmits<Emits>();

const marginAllocation = ref<IMarginAllocationV2>();
const isLoadingMarginAllocation = ref(true);
const isMarginAllocationDrawerOpen = ref(false);
const isModalOpen = ref(false);

const availableToOperateValue = computed(() => {
  return props.availableBalanceData?.availableToOperateValue?.formatted || '-';
});

const fetchMarginAllocation = async () => {
  isLoadingMarginAllocation.value = true;
  try {
    const marginAllocationResponse = await getMarginAllocationV2();
    marginAllocation.value = marginAllocationResponse;
  } catch (error) {
    console.error(error);
  } finally {
    isLoadingMarginAllocation.value = false;
  }
};

const shouldShowMarginAllocation = computed(() => {
  return props.hasMarginAllocation && marginAllocation.value;
});

const hasAllocatedValue = computed(() => {
  return Number(marginAllocation.value?.balance.value) > 0;
});

const emitOnRetry = () => {
  emit('on-retry');
};

const openModal = () => {
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
};

const goToMarginAllocation = () => {
  if (hasAllocatedValue.value) {
    isMarginAllocationDrawerOpen.value = true;
  } else {
    router.push({
      name: 'marginAllocation'
    });
  }
};

onMounted(() => {
  fetchMarginAllocation();
});
</script>

<style lang="less" scoped>
.available-balance {
  &__error {
    display: flex;
    justify-content: center;
  }
  &__loader {
    width: 110px;
    height: 25px;
    border-radius: @size-radius-x100;
  }

  &__margin-allocation {
    &-loader {
      width: 100%;
      height: 53px;
      margin-top: @size-spacing-x400;
    }

    &-row {
      margin-top: @size-spacing-x400;
    }

    &-value {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      gap: @size-spacing-x150;
    }

    &-text {
      color: @element-primary !important;
      font-weight: 600;
    }
  }
}
</style>
